import { DoubleRightOutlined } from "@ant-design/icons"
import { Tooltip, Tag, Button, Table } from "antd"
import { ColumnsType } from "antd/es/table"
import { FC, useState, useEffect, useMemo } from "react"
import { Link } from "react-router-dom"
import { EResultTagAdequacy } from "../../../components/commons-ts/common"
import { UUID, MatTypeEN12503, Zone } from "../../../generated/proto-ts/main"
import { MeanWithStd } from "../../../types"
import { useUsercommEquipmentChildrenRecursiveBLE } from "../../../usercomm/local/ble/usercommAsyncRequestBLE"
import { Translated } from "../../../utils/translated"
import { pbUUIDToUuid, COLOR_BG_ROW_DELETED } from "../../../utils/utils"
import { renderMeanStd } from "../../../utils/utils_tsx"
import { getZoneResultSM } from "../../../calculus/calculus_SM"
import { IZoneResult } from "../../../calculus/types"
import { TableImageThumbnail } from "../../../components/commons-ts/tableImageThumbnail"
import { useUsercommEquipmentChildrenRecursiveBimodal } from "../../../usercomm/common/usercommAsyncRequestBimodal"
import { useSyncronizationContext } from "../../../providers/syncronizationProvider"
import { IStatEnvelope, StatEnvelope } from "../../../utils/statEnvelope"

export interface IZoneProcessed_SM {
  zone: Zone
  nb_impacts: number
  ffhEnv: IStatEnvelope | null
  gmaxMstd: MeanWithStd | null
  deflectionDistanceMMMstd: MeanWithStd | null
  deflectionDistancePercMstd: MeanWithStd | null
  resiliencePercMstd: MeanWithStd | null
  result: IZoneResult | null
}

export const EquipmentZonesTableSM: FC<{
  equipmentUUID: UUID | null
  zones: Zone[] | null
  smType: MatTypeEN12503 | null
  smThickness: MeanWithStd | null
}> = ({ equipmentUUID, zones, smType, smThickness }) => {
  const [zoneImpactsMap, getEntitiesRecursiveRequest] =
    useUsercommEquipmentChildrenRecursiveBimodal()
  const [zonesProcessed, setZonesProcessed] = useState<IZoneProcessed_SM[] | null>(null)
  const { isAdmin } = useSyncronizationContext()

  useEffect(() => {
    getEntitiesRecursiveRequest(equipmentUUID)
  }, [equipmentUUID])

  useEffect(() => {
    if (zones === null || zoneImpactsMap === null) {
      return
    }

    const smThicknessMean = smThickness ? smThickness.mean : null

    let _zonesProcessed: IZoneProcessed_SM[] = []
    for (let zone of zones) {
      if (zone.deleted_at > 0) {
        continue
      }
      let zoneUUIDStr = pbUUIDToUuid(zone.uuid)
      let zoneImpacts = zoneImpactsMap[zoneUUIDStr]
      let nb_impacts = 0
      let ffhEnv = new StatEnvelope()
      let result: IZoneResult | null = null
      if (zoneImpacts !== undefined) {
        for (let impact of zoneImpacts) {
          if (impact.deleted_at > 0) {
            continue
          }
          nb_impacts++
          ffhEnv.addValue(impact.impact_ffh)
        }
        result = getZoneResultSM(zoneImpacts, smType, smThicknessMean)
      }

      _zonesProcessed.push({
        zone: zone,
        nb_impacts: nb_impacts,
        ffhEnv: ffhEnv,
        gmaxMstd: result?.deflectionDistanceMM ?? null,
        deflectionDistanceMMMstd: result?.deflectionDistanceMM ?? null,
        deflectionDistancePercMstd: result?.deflectionDistancePerc ?? null,
        resiliencePercMstd: result?.resiliencePerc ?? null,
        result: result,
      })
    }
    console.debug(`EquipmentZonesTableSM: processed ${_zonesProcessed.length} zones`)
    setZonesProcessed(_zonesProcessed)
  }, [zones, zoneImpactsMap, smType, smThickness])

  const columns = useMemo((): ColumnsType<IZoneProcessed_SM> => {
    return [
      {
        title: "📷", // Photo
        render: (_, zone) => {
          let pictureUUID = zone.zone.pictures[0]
          if (pictureUUID === undefined) {
            return <div style={{ width: "3rem" }} />
          }
          return (
            <Link to={`/zones/${pbUUIDToUuid(zone.zone.uuid)}`}>
              <TableImageThumbnail pictureUUID={pictureUUID} alt={zone.zone.zone_name} />
            </Link>
          )
        },
      },
      {
        key: "zone",
        title: "Zone",
        ellipsis: true,
        render: (_, zone) => {
          return (
            <Link
              to={`/zones/${pbUUIDToUuid(zone.zone.uuid)}`}
              style={{
                textDecoration: "none",
              }}
            >
              {zone.zone.zone_name}
            </Link>
          )
        },
      },
      {
        title: <Tooltip overlay={<Translated keyEn="# Impacts" />}># Imp</Tooltip>,
        render: (_, zone) => {
          return <span>{zone.nb_impacts}</span>
        },
      },
      // {
      //     key: "ffh",
      //     title: (
      //         <Tooltip
      //             overlay={
      //                 <Translated keyEn="Target Free Fall Height, m" />
      //             }
      //         >
      //             <div>
      //                 <Translated keyEn="Target FFH, m" />
      //             </div>
      //         </Tooltip>
      //     ),
      //     render: (_, zone) => {
      //         let valueMeters = zone.zone_ffh_max / 100
      //         if (valueMeters === 0) {
      //             return "N/A"
      //         }
      //         return <span>{valueMeters.toFixed(2)}</span>
      //     },
      // },
      {
        key: "measuredFFH",
        title: (
          <div>
            <Tooltip overlay={<Translated keyEn="Max Measured Free Fall Height, m" />}>
              <div>
                <Translated keyEn="Height, m" />
              </div>
            </Tooltip>
          </div>
        ),
        render: (_, zone) => {
          if (zone.ffhEnv === null) {
            return <Tag>N/A</Tag>
          }
          return <span>{zone.ffhEnv.format(2)}</span>
        },
      },
      {
        key: "gmax",
        title: <Translated keyEn="Gmax" />,
        render: (_, zone) => {
          if (zone.result === null || zone.result.gmax === null) {
            return <Tag>N/A</Tag>
          }
          return renderMeanStd(zone.result.gmax, 3, 1)
        },
      },
      {
        key: "deflectionDistanceMM",
        title: (
          <Tooltip overlay={<Translated keyEn="Deflection distance, mm" />}>
            <div>
              <Translated keyEn="Def. mm" />
            </div>
          </Tooltip>
        ),
        render: (_, zone) => {
          if (zone.result === null || zone.result.deflectionDistanceMM === null) {
            return <Tag>N/A</Tag>
          }
          return renderMeanStd(zone.result.deflectionDistanceMM, 3, 0)
        },
      },
      {
        key: "deflectionDistancePerc",
        title: (
          <Tooltip overlay={<Translated keyEn="Deflection percentage, %" />}>
            <div>
              <Translated keyEn="Def. %" />
            </div>
          </Tooltip>
        ),
        render: (_, zone) => {
          if (zone.result === null || zone.result.deflectionDistancePerc === null) {
            return <Tag>N/A</Tag>
          }
          return renderMeanStd(zone.result.deflectionDistancePerc, 3, 0)
        },
      },
      {
        key: "resiliencePerc",
        title: (
          <Tooltip overlay={<Translated keyEn="Resilience percentage, %" />}>
            <div>
              <Translated keyEn="Res. %" />
            </div>
          </Tooltip>
        ),
        render: (_, zone) => {
          if (zone.result === null || zone.result.resiliencePerc === null) {
            return <Tag>N/A</Tag>
          }
          return renderMeanStd(zone.result.resiliencePerc, 3, 1)
        },
      },
      {
        key: "result",
        title: <Translated keyEn="Result" />,
        render: (_, zone) => {
          let result = zone.result
          if (result === null) {
            return <Tag>N/A</Tag>
          }
          return <EResultTagAdequacy result={result.result} />
        },
      },
      {
        title: "",
        width: "2rem",
        render: (_, zone) => {
          return (
            <Link to={`/zones/${pbUUIDToUuid(zone.zone.uuid)}`}>
              <Button type="link" size="small">
                <DoubleRightOutlined />
              </Button>
            </Link>
          )
        },
      },
    ]
  }, [zoneImpactsMap])

  return (
    <Table
      dataSource={zonesProcessed ?? []}
      columns={columns}
      loading={zonesProcessed === null || zoneImpactsMap === null}
      pagination={false}
      rowKey={(e) => pbUUIDToUuid(e.zone.uuid)}
      size="small"
      scroll={{ x: true }}
      bordered
      onRow={(record) => {
        if (record.zone.deleted_at === 0) {
          return {}
        }
        if (isAdmin) {
          return {
            style: {
              backgroundColor: COLOR_BG_ROW_DELETED,
            },
          }
        } else {
          return {
            style: {
              display: "none",
            },
          }
        }
      }}
      footer={() => (
        <i>
          <Translated keyEn="Table of zones for EN 12503 - SPORTS MATS" />
        </i>
      )}
    />
  )
}

export const EquipmentZonesTableSM_Print: FC<{
  equipmentUUID: UUID | null
  zones: Zone[] | null
  smType: MatTypeEN12503 | null
  smThickness: MeanWithStd | null
}> = ({ equipmentUUID, zones, smType, smThickness }) => {
  const [zoneImpactsMap, getEntitiesRecursiveRequest] =
    useUsercommEquipmentChildrenRecursiveBimodal()

  const [zonesProcessed, setZonesProcessed] = useState<IZoneProcessed_SM[] | null>(null)

  useEffect(() => {
    getEntitiesRecursiveRequest(equipmentUUID)
  }, [equipmentUUID])

  useEffect(() => {
    if (zones === null || zoneImpactsMap === null) {
      return
    }

    const smThicknessMean = smThickness ? smThickness.mean : null

    let _zonesProcessed: IZoneProcessed_SM[] = []
    for (let zone of zones) {
      if (zone.deleted_at !== 0) {
        continue
      }
      let zoneUUIDStr = pbUUIDToUuid(zone.uuid)
      let zoneImpacts = zoneImpactsMap[zoneUUIDStr]
      let nb_impacts = 0
      let ffhEnv = new StatEnvelope()
      let result: IZoneResult | null = null
      if (zoneImpacts !== undefined) {
        for (let impact of zoneImpacts) {
          if (impact.deleted_at === 0) {
            nb_impacts++
            ffhEnv.addValue(impact.impact_ffh)
          }
        }
        result = getZoneResultSM(zoneImpacts, smType, smThicknessMean)
      }

      _zonesProcessed.push({
        zone: zone,
        nb_impacts: nb_impacts,
        ffhEnv: ffhEnv,
        gmaxMstd: result?.deflectionDistanceMM ?? null,
        deflectionDistanceMMMstd: result?.deflectionDistanceMM ?? null,
        deflectionDistancePercMstd: result?.deflectionDistancePerc ?? null,
        resiliencePercMstd: result?.resiliencePerc ?? null,
        result: result,
      })
    }
    setZonesProcessed(_zonesProcessed)
  }, [zones, zoneImpactsMap, smType, smThickness])

  const columns = useMemo((): ColumnsType<IZoneProcessed_SM> => {
    return [
      {
        key: "zone",
        title: "Zone",
        ellipsis: true,
        render: (_, zone) => {
          return zone.zone.zone_name
        },
        sorter: (a, b) => a.zone.zone_name.localeCompare(b.zone.zone_name),
        defaultSortOrder: "ascend",
      },
      {
        title: "# Imp",
        render: (_, zone) => {
          return <span>{zone.nb_impacts}</span>
        },
        width: 50,
      },
      {
        key: "measuredFFH",
        title: <Translated keyEn="Height, m" />,
        render: (_, zone) => {
          if (zone.ffhEnv === null) {
            return <Tag>N/A</Tag>
          }
          return <span>{zone.ffhEnv.format(2)}</span>
        },
      },
      {
        key: "gmax",
        title: <Translated keyEn="Gmax, g" />,
        render: (_, zone) => {
          if (zone.result === null || zone.result.gmax === null) {
            return <Tag>N/A</Tag>
          }
          return renderMeanStd(zone.result.gmax, 3, 1)
        },
        width: 70,
      },
      {
        key: "deflectionDistanceMM",
        title: <Translated keyEn="Deflection, mm" />,
        render: (_, zone) => {
          if (zone.result === null || zone.result.deflectionDistanceMM === null) {
            return <Tag>N/A</Tag>
          }
          return renderMeanStd(zone.result.deflectionDistanceMM, 3, 0)
        },
      },
      {
        key: "deflectionDistancePerc",
        title: <Translated keyEn="Deflection, %" />,
        render: (_, zone) => {
          if (zone.result === null || zone.result.deflectionDistancePerc === null) {
            return <Tag>N/A</Tag>
          }
          return renderMeanStd(zone.result.deflectionDistancePerc, 3, 0)
        },
      },
      {
        key: "resiliencePerc",
        title: <Translated keyEn="Resilience, %" />,
        render: (_, zone) => {
          if (zone.result === null || zone.result.resiliencePerc === null) {
            return <Tag>N/A</Tag>
          }
          return renderMeanStd(zone.result.resiliencePerc, 3, 1)
        },
      },
      {
        key: "result",
        title: <Translated keyEn="Result" />,
        render: (_, zone) => {
          let result = zone.result
          if (result === null) {
            return <Tag>N/A</Tag>
          }
          return <EResultTagAdequacy result={result.result} />
        },
        width: 140,
      },
    ]
  }, [zonesProcessed, zoneImpactsMap])

  return (
    <Table
      dataSource={zonesProcessed ?? []}
      columns={columns}
      loading={zonesProcessed === null || zoneImpactsMap === null}
      pagination={false}
      rowKey={(e) => pbUUIDToUuid(e.zone.uuid)}
      size="small"
      bordered
      showSorterTooltip={false}
      style={{
        width: "100%",
      }}
    />
  )
}
